import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteFeatures } from "@/hooks/useSiteFeatures";
import {
  buildPublicLoginPath,
  buildPublicSignupPath,
  isPublicMarketingPath,
  PUBLIC_FOOTER_GROUPS,
  resolvePublicAppPath,
} from "@/lib/public-marketing";

type FooterProps = {
  className?: string;
};

export function Footer({ className }: FooterProps) {
  const location = useLocation();
  const { user, profile, isAdmin } = useAuth();
  const { toofiEnabled, toofiPlansEnabled } = useSiteFeatures();
  const isPublicFooter = isPublicMarketingPath(location.pathname);
  const publicSurfaceParam = location.pathname === "/" ? "home" : location.pathname.replace(/^\//, "");
  const authenticatedPath = resolvePublicAppPath(Boolean(user), profile?.account_type, isAdmin);
  const publicFooterGroups = PUBLIC_FOOTER_GROUPS.map((group) => ({
    ...group,
    links: group.links.filter((link) => {
      if (link.href === "/toofi") return toofiEnabled;
      if (link.href === "/pricing") return toofiPlansEnabled;
      return true;
    }),
  })).filter((group) => group.links.length > 0);

  const resolveFooterHref = (href: string) => {
    if (href === "/auth/signup") {
      if (authenticatedPath) return authenticatedPath;
      return buildPublicSignupPath({ source: "footer", surface: publicSurfaceParam, cta: "start-free" });
    }

    if (href === "/auth/login") {
      if (authenticatedPath) return authenticatedPath;
      return buildPublicLoginPath({ source: "footer", surface: publicSurfaceParam, cta: "sign-in" });
    }

    return href;
  };

  if (!isPublicFooter) {
    return (
      <div className={`relative z-10 mt-auto flex w-full justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:px-4 ${className || ""}`}>
        <footer className="w-full max-w-fit rounded-full border border-[rgba(226,232,240,0.8)] bg-[linear-gradient(180deg,rgba(255,255,255,0.86),rgba(248,250,252,0.78))] px-3 py-2 shadow-[0_16px_36px_rgba(15,23,42,0.05)] backdrop-blur-2xl">
          <div className="flex flex-wrap items-center justify-center gap-1">
            <span className="px-3 py-1.5 text-[12px] font-medium text-[var(--athr-text-tertiary)]">&copy; 2026 Athr</span>
            <Link to="/privacy" className="rounded-full px-3 py-1.5 text-center text-[12px] font-medium leading-tight text-[var(--athr-text-secondary)] transition-colors hover:bg-[var(--athr-surface-soft)] hover:text-[var(--athr-text-primary)]">
              Privacy
            </Link>
            <Link to="/terms" className="rounded-full px-3 py-1.5 text-center text-[12px] font-medium leading-tight text-[var(--athr-text-secondary)] transition-colors hover:bg-[var(--athr-surface-soft)] hover:text-[var(--athr-text-primary)]">
              Terms
            </Link>
            <Link to="/contact" className="rounded-full px-3 py-1.5 text-center text-[12px] font-medium leading-tight text-[var(--athr-text-secondary)] transition-colors hover:bg-[var(--athr-surface-soft)] hover:text-[var(--athr-text-primary)]">
              Contact
            </Link>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className={`relative z-10 mt-auto flex w-full justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4 sm:px-4 ${className || ""}`}>
      <footer
        className="
        w-full max-w-6xl
        rounded-[1.6rem] border border-[rgba(226,232,240,0.8)]
        bg-[linear-gradient(180deg,rgba(255,255,255,0.86),rgba(248,250,252,0.78))]
        px-3 py-3 shadow-[0_16px_36px_rgba(15,23,42,0.05)] backdrop-blur-2xl
        sm:rounded-[1.8rem] sm:px-5 sm:py-4
      "
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-center text-[12px] font-medium text-[var(--athr-text-tertiary)] sm:text-left">&copy; 2026 Athr</p>
            <p className="text-center text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--athr-text-tertiary)] sm:text-right">
              Student-first academic flow
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {publicFooterGroups.map((group) => (
              <div key={group.title} className="public-panel rounded-[1.25rem] border border-[rgba(226,232,240,0.76)] bg-white/72 px-3 py-3">
                <p className="px-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--athr-text-tertiary)]">{group.title}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {group.links.map((link) => (
                    <Link
                      key={link.href}
                      to={resolveFooterHref(link.href)}
                      className="rounded-full px-3 py-1.5 text-center text-[12px] font-medium leading-tight text-[var(--athr-text-secondary)] transition-colors hover:bg-[var(--athr-surface-soft)] hover:text-[var(--athr-text-primary)]"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
